// =============================================================================
// Investor Distributions Page
// =============================================================================

import { useEffect, useState } from 'react';
import {
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui';
import { investorService, type DistributionItem } from '../../services/investor.service';
import type { Deal } from '../../types';
import { formatDate } from '../../utils/dateFormat';

// =============================================================================
// Types
// =============================================================================

// Using DistributionItem from investor.service.ts

// =============================================================================
// Component
// =============================================================================

export function DistributionsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [distributions, setDistributions] = useState<DistributionItem[]>([]);
  const [filteredDistributions, setFilteredDistributions] = useState<DistributionItem[]>(
    []
  );
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stats, setStats] = useState({
    totalDistributed: 0,
    thisYearDistributed: 0,
    lastDistributionDate: '',
    distributionCount: 0,
  });

  // Filters
  const [selectedDeal, setSelectedDeal] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    loadDistributions();
  }, []);

  useEffect(() => {
    filterDistributions();
  }, [distributions, selectedDeal, selectedYear, selectedType]);

  const loadDistributions = async () => {
    setIsLoading(true);
    try {
      const response = await investorService.getDistributions();
      const distList = response.distributions;

      // Extract unique deals from distributions
      const uniqueDeals = new Map<string, Deal>();
      distList.forEach(d => {
        if (d.distribution.deal && !uniqueDeals.has(d.distribution.deal.id)) {
          uniqueDeals.set(d.distribution.deal.id, {
            id: d.distribution.deal.id,
            name: d.distribution.deal.name,
            address: '',
            city: d.distribution.deal.city || '',
            state: d.distribution.deal.state || '',
            zipCode: '',
            propertyType: 'OTHER',
            totalEquityRaised: 0,
            isActive: true,
          });
        }
      });

      setDeals(Array.from(uniqueDeals.values()));
      setDistributions(distList);

      // Calculate stats
      const totalDistributed = distList.reduce((sum, d) => sum + d.amount, 0);
      const currentYear = new Date().getFullYear();
      const thisYearDistributed = distList
        .filter((d) => new Date(d.distribution.distributionDate).getFullYear() === currentYear)
        .reduce((sum, d) => sum + d.amount, 0);

      const sortedByDate = [...distList].sort(
        (a, b) => new Date(b.distribution.distributionDate).getTime() - new Date(a.distribution.distributionDate).getTime()
      );

      setStats({
        totalDistributed,
        thisYearDistributed,
        lastDistributionDate: sortedByDate[0]?.distribution.distributionDate || '',
        distributionCount: distList.length,
      });
    } catch (error) {
      console.error('Failed to load distributions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterDistributions = () => {
    let filtered = [...distributions];

    // Filter by deal
    if (selectedDeal !== 'all') {
      filtered = filtered.filter((d) => d.distribution.dealId === selectedDeal);
    }

    // Filter by year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(
        (d) => new Date(d.distribution.distributionDate).getFullYear().toString() === selectedYear
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter((d) => d.distribution.type === selectedType);
    }

    // Sort by date (newest first)
    filtered.sort(
      (a, b) => new Date(b.distribution.distributionDate).getTime() - new Date(a.distribution.distributionDate).getTime()
    );

    setFilteredDistributions(filtered);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Using centralized PST date formatting from utils/dateFormat

  const getDistributionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CASH_FLOW: 'Cash Flow',
      REFINANCE: 'Refinance',
      SALE: 'Sale Proceeds',
      RETURN_OF_CAPITAL: 'Return of Capital',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getDistributionTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      CASH_FLOW: 'bg-green-100 text-green-800',
      REFINANCE: 'bg-blue-100 text-blue-800',
      SALE: 'bg-purple-100 text-purple-800',
      RETURN_OF_CAPITAL: 'bg-yellow-100 text-yellow-800',
      OTHER: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      ACH: 'ACH Transfer',
      CHECK: 'Check',
      WIRE: 'Wire Transfer',
    };
    return labels[method] || method;
  };

  // Get unique years from distributions
  const years = [
    ...new Set(distributions.map((d) => new Date(d.distribution.distributionDate).getFullYear())),
  ].sort((a, b) => b - a);

  const distributionTypes = ['CASH_FLOW', 'REFINANCE', 'SALE', 'RETURN_OF_CAPITAL'];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Distributions</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track your investment returns and distribution history
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Distributed</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(stats.totalDistributed)}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">This Year ({new Date().getFullYear()})</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(stats.thisYearDistributed)}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Distribution</p>
              <p className="text-lg font-bold text-gray-900">
                {stats.lastDistributionDate ? formatDate(stats.lastDistributionDate) : 'N/A'}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Payments</p>
              <p className="text-lg font-bold text-gray-900">{stats.distributionCount}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Deal Filter */}
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Property</label>
            <select
              value={selectedDeal}
              onChange={(e) => setSelectedDeal(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Properties</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>
                  {deal.name}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div className="w-full md:w-40">
            <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Years</option>
              {years.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="w-full md:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              {distributionTypes.map((type) => (
                <option key={type} value={type}>
                  {getDistributionTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {filteredDistributions.length} of {distributions.length} distributions
        </p>
        <p className="text-sm font-medium text-green-600">
          Total: {formatCurrency(filteredDistributions.reduce((sum, d) => sum + d.amount, 0))}
        </p>
      </div>

      {/* Distributions List */}
      {filteredDistributions.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Distributions Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {selectedDeal !== 'all' || selectedYear !== 'all' || selectedType !== 'all'
                ? 'Try adjusting your filters'
                : 'Your distributions will appear here once processed'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDistributions.map((dist) => (
            <Card key={dist.id} padding="md">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex p-3 bg-green-100 rounded-xl">
                    <BanknotesIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">{dist.distribution.notes || getDistributionTypeLabel(dist.distribution.type)}</h3>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDistributionTypeColor(
                          dist.distribution.type
                        )}`}
                      >
                        {getDistributionTypeLabel(dist.distribution.type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <BuildingOfficeIcon className="h-4 w-4" />
                        {dist.distribution.deal.name}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {formatDate(dist.distribution.distributionDate)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {getPaymentMethodLabel(dist.distribution.paymentMethod)}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(dist.amount)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {((dist.amount / dist.distribution.totalAmount) * 100).toFixed(2)}% of {formatCurrency(dist.distribution.totalAmount)} total
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Year Summary */}
      {years.length > 0 && (
        <Card padding="md">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Annual Summary</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Year
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Payments
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Total Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {years.map((year) => {
                  const yearDistributions = distributions.filter(
                    (d) => new Date(d.distribution.distributionDate).getFullYear() === year
                  );
                  const yearTotal = yearDistributions.reduce((sum, d) => sum + d.amount, 0);
                  return (
                    <tr key={year} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{year}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">
                        {yearDistributions.length}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right">
                        {formatCurrency(yearTotal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default DistributionsPage;
